import * as React from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import { sanitizeHtml } from '@/lib/sanitize-html';
import { Breadcrumb, Button, Card, CardContent, CardHeader, CardTitle, Skeleton, SkeletonText } from '@/components/ui';

type Article = {
  id: string;
  title: string;
  content?: string;
  folder?: { name?: string; category?: { name?: string } };
};

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']) {
  return error instanceof ApiError ? error.message : t('dir.kb.articleLoadError');
}

// KB-READ: agent/admin-facing knowledge base article reader. Reuses the same
// public /kb/articles/:id endpoint the portal uses (the backend grants agents
// the widest visibility incl. agents_only), but lives inside the agent AppShell
// so agents/admins clicking a suggested article aren't bounced by the
// customer-only /portal/kb route guard.
export function AgentKbArticlePage() {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = React.useState<Article | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      setArticle(await api.get<Article>(`/kb/articles/${id}`));
    } catch (e) {
      setError(errorMessage(e, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  React.useEffect(() => { void load(); }, [load]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <Skeleton className="h-4 w-40" />
        <Card><CardContent className="space-y-4 pt-6" aria-busy="true"><Skeleton className="h-6 w-3/5" /><SkeletonText lines={6} /></CardContent></Card>
      </div>
    );
  }

  if (error || !article) {
    return (
      <Card>
        <CardContent className="flex items-center justify-between gap-4 p-6">
          <p className="text-sm text-danger-600">{error || t('dir.kb.articleNotFound')}</p>
          <Button variant="outline" onClick={load}>{t('dir.kb.retry')}</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Breadcrumb items={[{ label: t('dir.kb.breadcrumb'), to: '/settings/kb' }, { label: article.title }]} />
      <Card>
        <CardHeader>
          <CardTitle>{article.title}</CardTitle>
          <p className="text-xs text-ink-subtle">{article.folder?.category?.name} · {article.folder?.name}</p>
        </CardHeader>
        <CardContent>
          <div className="prose max-w-none text-sm" dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.content || '') }} />
        </CardContent>
      </Card>
    </div>
  );
}
